import { useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import * as utils from 'Utils';
import { withLogin } from 'Components/hocs';
import IconSvg from 'Components/IconSvg';
import ProgressBar from 'Components/ProgressBar';
const OAUTH_URL = process.env.REACT_APP_OAUTH_URL;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const LoginSuccess = (props) => {
  const { uim, applicationConfig, server_state, strings } = props;
  const {
    application,
    encryptionKey,
    gleamId,
    includePayload,
    originoverride,
    redirectTo,
    redirect_url,
    token,
  } = utils.getQueryObject(props);

  const gleamCallback = (userId) => {
    const { redirectTo } = utils.getQueryObject(props);
    window.Gleam = window.Gleam || [];
    window.Gleam.push(['userId', userId]);
    if (redirectTo) utils.postMessageHelper.redirect(redirectTo);
  };

  const getPathName = async () => {
    const pathName = redirectTo?.[0] !== '/' ? '' : redirectTo;
    if (includePayload) {
      if (encryptionKey) {
        const uimClientInfo = await uim.getUimClientInfo();
        const paramString = await utils.encryptUserData(
          encryptionKey,
          uimClientInfo
        );
        return utils.appendQueryString(pathName, `rbuser=${paramString}`);
      } else {
        // We are including the user's payload in the querystring via a redirect
        const params = await utils.createUimRedirectParams(uim);
        return utils.appendQueryString(pathName, params);
      }
    }

    return pathName;
  };

  const onSuccess = async () => {
    try {
      // OAuth 2.0 IMPLEMENTATION
      // Will be recognized with the 'server_state' url param
      if (server_state) {
        const { id_token, access_token } = await uim.getUimClientInfo();
        window.location.href = `${OAUTH_URL}/${server_state}?id_token=${id_token}&access_token=${access_token}`;
      }
      // BEVY INTEGRATION
      // Hard coding this route to bevy only for now
      else if (utils.isBevy(applicationConfig)) {
        const uimClientInfo = await uim.getUimClientInfo();
        axios
          .post(`${API_BASE_URL}/api/v1/encrypt`, {
            ...uimClientInfo,
            token,
          })
          .then((x) => x.data)
          .then((resp) => {
            let pathName = redirect_url;
            if (pathName && pathName[0] !== '/') {
              pathName = '';
            }
            const params = `sso_auth_status=ok&sso_token=${resp.sso_token}`;
            const path = utils.appendQueryString(pathName, params);
            utils.postMessageHelper.redirect(path);
          });
      }
      // GLEAM INTEGRATION
      else if (gleamId != null && gleamId !== '') {
        const uimClientInfo = await uim.getUimClientInfo();
        utils.installScript({
          onLoadCallback: () => gleamCallback(uimClientInfo.uid),
          scriptSrc: `https://gleam.io/${gleamId}/trk.js`,
          uniqueScriptId: 'gleam',
        });
      }
      // SPECIAL REDIRECT FOR REDBULL.COM
      else if (originoverride && application.toLowerCase() === 'n4igw1aat') {
        const pathName = await getPathName();
        const allowedUrls = [
          'https://*.redbull.com',
          'https://*.redbullaws.com',
          'https://rbcom-dev*.herokuapp.com',
          'https://rbcom-staging*.herokuapp.com',
          'http://localhost*',
        ];
        const allowed = allowedUrls.filter((aU) =>
          utils.convert2RegExp(aU).test(originoverride)
        ).length;
        if (!allowed) throw new Error('This origin is not allowed');
        window.location.href = `${originoverride}${pathName}`;
      }
      // USER IS ON MOBILE RED BULL TV APP, SEND REDBULLTV URL
      else if (utils.isRedBullTVApp(applicationConfig)) {
        window.location.href = await utils.createMobileDeeplink(uim);
      }
      // USER IS ON MOBILE APP
      else if (utils.isMobileApp(applicationConfig)) {
        window.location.href = await utils.createMobileDeeplink(
          uim,
          encryptionKey,
          applicationConfig.url
        );
      }
      // USER IS USING POP UP LOGIN METHOD
      else if (utils.postMessageHelper.checkForSpawner() && !redirectTo) {
        const uimClientInfo = await uim.getUimClientInfo();
        utils.postMessageHelper.send({
          type: utils.postMessageHelper.types.LOGIN_SUCCESS,
          payload: uimClientInfo,
        });
      }
      // USER IS USING REDIRECT LOGIN METHOD
      else if (redirectTo) {
        const pathName = await getPathName();
        utils.postMessageHelper.redirect(pathName);
      }
    } catch (err) {
      utils.logException(err);
    }
  };

  useEffect(() => {
    onSuccess();
  }, []);

  return (
    <>
      <ProgressBar />
      <div className="icon-heading">
        <IconSvg type="checkmark" className="icon--success" />
      </div>
      <h1 className="page-heading">{strings.successfulLogin}</h1>
    </>
  );
};

LoginSuccess.propTypes = {
  applicationConfig: PropTypes.object.isRequired,
  server_state: PropTypes.string.isRequired,
  strings: PropTypes.object.isRequired,
  uim: PropTypes.object.isRequired,
};

export default withLogin(LoginSuccess);
